import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { api } from '../api';
import GlassCard from './ui/GlassCard';
import Badge from './ui/Badge';
import Btn from './ui/Btn';
import ConfirmDialog from './ui/ConfirmDialog';
import EmptyState from './ui/EmptyState';
import Header from './ui/Header';
import SearchBar from './ui/SearchBar';
import InfoTable from './ui/InfoTable';
import Icon from './ui/Icon';
import { formatDate, formatMoney } from '../utils/format';
import PhotoSlider from './ui/PhotoSlider';
import KitLink, { KitLinkFields } from './ui/KitLink';

interface ArchiveItem extends KitLinkFields {
  id: number;
  original_repair_id?: number;
  repair_number?: string;
  client_name?: string;
  city?: string;
  device: string;
  defect?: string;
  work_done?: string;
  cost: number;
  paid: boolean;
  deleted_at: string;
  delete_reason?: string;
  image_paths?: string;
}

interface ArchiveSectionProps {
  title?: string;
  // Панель переключения разделом-хозяином: архив живёт внутри «Ремонтов».
  toolbar?: React.ReactNode;
  onOpenKit?: (kitId: number, archived: boolean) => void;
  // Переход из комплектации: открыть архивную запись по её id.
  openId?: number | null;
  onOpened?: () => void;
}

const ArchiveSection: React.FC<ArchiveSectionProps> = ({ title = 'Архив', toolbar, onOpenKit, openId, onOpened }) => {
  const [archive, setArchive] = useState<ArchiveItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<ArchiveItem | null>(null);
  const [confirmRestore, setConfirmRestore] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [search, setSearch] = useState('');

  const loadArchive = useCallback(async () => {
    try {
      const res = await api.getArchive();
      setArchive(res.data);
    } catch (err) {
      console.error('Error loading archive', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadArchive();
  }, [loadArchive]);

  useEffect(() => {
    if (!openId || loading) return;
    const item = archive.find(a => a.id === openId);
    if (item) setSelected(item);
    onOpened?.();
  }, [openId, loading, archive, onOpened]);

  const displayed = useMemo(() => {
    if (!search) return archive;
    const q = search.toLowerCase();
    return archive.filter(a =>
      a.device.toLowerCase().includes(q) ||
      (a.repair_number || '').includes(q) ||
      (a.client_name || '').toLowerCase().includes(q)
    );
  }, [archive, search]);

  const total = displayed.reduce((s, a) => s + Number(a.cost || 0), 0);

  const handleRestore = async () => {
    if (!selected) return;
    try {
      await api.restoreRepair(selected.id);
      setArchive(a => a.filter(x => x.id !== selected.id));
      setSelected(null);
      setConfirmRestore(false);
    } catch (err) {
      console.error('Restore error', err);
    }
  };

  const handleDeletePermanent = async () => {
    if (!selected) return;
    try {
      await api.deleteFromArchive(selected.id);
      setArchive(a => a.filter(x => x.id !== selected.id));
      setSelected(null);
      setConfirmDelete(false);
    } catch (err) {
      console.error('Delete error', err);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Header title={title} />
        {toolbar && (
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--glass-border)', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {toolbar}
          </div>
        )}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text2)' }}>
          Загрузка...
        </div>
      </div>
    );
  }

  if (selected) {
    const images: string[] = selected.image_paths
      ? (() => { try { return JSON.parse(selected.image_paths); } catch { return []; } })()
      : [];

    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {confirmRestore && (
          <ConfirmDialog
            message={`Восстановить ремонт из архива?`}
            onConfirm={handleRestore}
            onCancel={() => setConfirmRestore(false)}
            confirmLabel="Восстановить"
          />
        )}
        {confirmDelete && (
          <ConfirmDialog
            message={`Удалить окончательно? Это действие нельзя отменить!`}
            onConfirm={handleDeletePermanent}
            onCancel={() => setConfirmDelete(false)}
            confirmLabel="Удалить навсегда"
          />
        )}
        <Header title={selected.repair_number || `#${selected.original_repair_id || selected.id}`} onBack={() => setSelected(null)} />
        <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
          {images.length > 0 && <PhotoSlider images={images} />}
          <div style={{ marginBottom: 16 }}>
            <h2 style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--text0)', marginBottom: 10 }}>
              {selected.device}
            </h2>
            <Badge variant="paid">Архив</Badge>
          </div>
          <InfoTable rows={[
            ['Клиент', selected.client_name || '—'],
            ['Город', selected.city || '—'],
            ['Комплектация', <KitLink repair={selected} onOpenKit={onOpenKit} />],
            ['Неисправность', selected.defect || '—'],
            ['Работы', selected.work_done || '—'],
            ['Стоимость', (
              <span style={{ fontFamily: 'var(--mono)', fontWeight: 700, color: 'var(--teal)' }}>
                {formatMoney(selected.cost)}
              </span>
            )],
            ['Архивирован', formatDate(selected.deleted_at)],
            selected.delete_reason ? ['Причина', selected.delete_reason] : null,
          ]} />
          <div style={{ marginTop: 16, background: 'var(--glass-bg)', borderRadius: 'var(--r-sm)', padding: '10px 14px', fontSize: 12, color: 'var(--text2)', textAlign: 'center', border: '1px solid var(--glass-border)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}>
            Архивный ремонт · только просмотр
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <Btn variant="subtle" icon="back" onClick={() => setConfirmRestore(true)} style={{ flex: 1, justifyContent: 'center' }}>
              Восстановить
            </Btn>
            <Btn variant="danger" icon="trash" onClick={() => setConfirmDelete(true)} style={{ flex: 1, justifyContent: 'center' }}>
              Удалить навсегда
            </Btn>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Header title={title} />
      <div style={{
        margin: '12px 16px 0',
        background: 'var(--teal-dim)',
        border: '1px solid var(--teal-mid)',
        borderRadius: 'var(--r-md)',
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
      }}>
        <Icon name="archive" size={18} color="var(--teal)" />
        <div>
          <div style={{ fontSize: 11, color: 'var(--teal)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Итого в архиве
          </div>
          <div style={{ fontFamily: 'var(--mono)', fontWeight: 700, fontSize: 18, color: 'var(--teal)' }}>
            {formatMoney(total)}
          </div>
        </div>
      </div>
      <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--glass-border)', display: 'flex', flexDirection: 'column', gap: 10 }}>
        <SearchBar value={search} onChange={setSearch} placeholder="Устройство, клиент, номер..." />
        {toolbar && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>{toolbar}</div>
        )}
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
        {displayed.length === 0 ? (
          <EmptyState
            icon="archive"
            title={search ? 'Ничего не найдено' : 'Архив пуст'}
            sub={search ? 'Попробуйте другой запрос' : 'Архивные ремонты появятся здесь'}
          />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {displayed.map(a => (
              <GlassCard key={a.id} onClick={() => setSelected(a)} style={{ padding: '14px 16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text2)', marginBottom: 4 }}>
                      {a.repair_number || `#${a.original_repair_id || a.id}`}
                    </div>
                    <div style={{ fontWeight: 600, marginBottom: 4, color: 'var(--text0)' }}>{a.device}</div>
                    <div style={{ color: 'var(--text2)', fontSize: 13 }}>{a.client_name || '—'}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontFamily: 'var(--mono)', fontWeight: 700, color: 'var(--text0)' }}>{formatMoney(a.cost)}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'var(--mono)', marginTop: 4 }}>
                      {formatDate(a.deleted_at)}
                    </div>
                  </div>
                </div>
              </GlassCard>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ArchiveSection;
